<script lang="ts" setup>

  type Props = {
    centered?: boolean
    topIcon?: string
    titleIcon?: string
    title?: string
    mainLinkName?: string
    mainLinkUrl?: string
    secondLinkName?: string
    secondLinkUrl?: string
    noFaq?: boolean
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    (e: 'first-link-click' | 'second-link-click'): void
  }>()

  function handleFirstLinkClick() {
    emit('first-link-click')
  }

  function handleSecondLinkClick() {
    emit('second-link-click')
  }

</script>

<template>
  <div
    class="c-action-block"
    :class="{ centered: props.centered }"
  >
    <SvgIcon
      v-if="props.topIcon"
      class="top-icon"
      :icon="props.topIcon"
      :height="35"
      :width="35"
    />
    <div class="header">
      <span class="title-icon">
        <slot name="title-icon" />
        <img
          v-if="props.titleIcon"
          :alt="props.titleIcon"
          :src="`logos/${props.titleIcon}.png`"
        >
      </span>
      <div
        class="title"
        v-html="props.title"
      />
    </div>
    <span
      v-if="props.mainLinkName && props.mainLinkUrl"
      class="main-link"
    >
      <Button
        type="primary"
        @click="handleFirstLinkClick"
      >{{ props.mainLinkName }}
      </Button>
    </span>
    <span
      v-if="props.secondLinkName && props.secondLinkUrl && !props.noFaq"
      class="second-link"
      @click="handleSecondLinkClick"
    >
      {{ secondLinkName }}
    </span>
    <slot class="slot" />
  </div>
</template>

<style scoped>
.c-action-block {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  padding: 2rem 1.5rem 3rem;
  margin-top: 3rem;
  background-color: var(--color-secondary-light);

  .top-icon {
    position: absolute;
    top: -1.875rem;
    left: 1.5rem;
    display: flex;
    padding: 0.75rem;
    border-radius: 0.5rem;
    background-color: var(--color-secondary);
    color: white;
  }

  .header {
    position: relative;
    display: flex;
    max-width: 20em;
    flex-direction: row;

    .title {
      color: var(--color-secondary);
      font-family: Bariol, sans-serif;
      font-size: 1.5em;
      font-weight: 700;
      text-align: left;

      &:deep(> br) {
        display: none;
      }
    }

    .title-icon {
      width: 100px;
      height: 80px;

      img {
        display: flex;
        width: auto;
        height: 100%;
        padding: 0.5em;
        border-radius: 10px;
        margin: 0 2.5em 1em 0;
        background-color: white;
      }
    }
  }

  .slot {
    width: 100%;
    max-width: 20em;
  }

  .main-link {
    display: flex;
    width: 100%;
    margin-top: 2rem;

    .c-button {
      width: 100%;
      font-size: 1.5rem;
      letter-spacing: 1.29px;
      line-height: 1.29;
      text-transform: uppercase;
    }
  }

  .second-link {
    display: flex;
    margin: 1.5rem auto 0;
    color: var(--color-secondary);
    font-family: Bariol, sans-serif;
    font-size: 1.125em;
    font-weight: 400;
    letter-spacing: 1.44px;
    line-height: 1.28;
    text-decoration: underline;
    text-transform: uppercase;
  }

  @media screen and (width >= 1024px) {
    display: flex;
    flex-direction: row;
    align-items: center;

    .header {
      position: relative;
      max-width: 30em;

      .title {
        position: relative;
        max-width: 20em;
        margin: auto 0;
        font-size: 2em;

        &:deep(> br) {
          display: block;
        }
      }

      .title-icon {
        width: 160px;
        height: 120px;
      }
    }

    &:deep(.c-app-links) {
      max-width: 30rem;
      justify-content: space-evenly;
      margin: 0;
      transform: scale(1.35);

      .c-button {
        margin: 0;
      }
    }
  }

  &.centered {
    width: 25rem;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding-top: 0;
    margin-top: 0;

    .title-icon {
      display: none;
    }

    .title {
      text-align: center;
    }

    .top-icon {
      position: relative;
      left: 0;
    }

    .c-button .button .primary {
      margin-top: 1em;
    }
  }
}
</style>
